import React, { useState, useMemo, useCallback } from 'react';
import { Box, Text, Popover, Checkbox, ScrollArea } from '@mantine/core';
import { IconX } from '@tabler/icons';
import { useHover } from '@mantine/hooks';
import { capitalizeFirstLetter } from '../../../../utils/capitalizeFirstLetter';
import { useIdeaFilters } from '../../../../hooks/useIdeaFilters';
import { findFiltersByPath } from '../../../../context/helpers';
import { normalizeOptions, formatSelectedValuesWithLabels } from '../../../../utils/filterUtils';

interface AppliedFilterChipProps {
  filter: any;
  onRemove: (filterName: string) => void;
  onUpdateSelected: (filterName: string, newSelected: string[]) => void;
}

const AppliedFilterChip: React.FC<AppliedFilterChipProps> = ({ filter, onRemove, onUpdateSelected }) => {
  const { hovered, ref } = useHover();
  const [popoverOpened, setPopoverOpened] = useState(false);
  const { ideasFiltersConfig } = useIdeaFilters();

  const label = filter.label || filter.name || filter.parent || '';
  const resolvedOptions = useMemo(() => {
    if (filter.options && (Array.isArray(filter.options) || Object.keys(filter.options || {}).length > 0)) {
      return filter.options;
    }
    if (!ideasFiltersConfig) return null;

    const candidates = [filter.parent, filter.name].filter(Boolean);
    for (const key of candidates) {
      const ideaByPath = findFiltersByPath(ideasFiltersConfig, `idea.${key}`);
      if (ideaByPath?.options) return ideaByPath.options;
      const evidenceByPath = findFiltersByPath(ideasFiltersConfig, `evidence.${key}`);
      if (evidenceByPath?.options) return evidenceByPath.options;

      const ideaDirect = ideasFiltersConfig?.idea?.[key];
      if (ideaDirect?.options) return ideaDirect.options;
      const evidenceDirect = ideasFiltersConfig?.evidence?.[key];
      if (evidenceDirect?.options) return evidenceDirect.options;
    }

    return null;
  }, [filter, ideasFiltersConfig]);

  const availableOptions = useMemo(() => normalizeOptions(resolvedOptions), [resolvedOptions]);
  const optionLabelByValue = useMemo(() => {
    const map = new Map<string, string>();
    availableOptions.forEach((opt) => {
      map.set(String(opt.value).toLowerCase(), String(opt.label));
    });
    return map;
  }, [availableOptions]);
  const values = useMemo(
    () => formatSelectedValuesWithLabels(filter, optionLabelByValue),
    [filter, optionLabelByValue]
  );
  const hasDropdown = availableOptions.length > 0 && Array.isArray(filter.selected);

  const originalCaseMap = useMemo(() => {
    const map = new Map<string, string>();
    if (Array.isArray(filter.selected)) {
      filter.selected.forEach((v: any) => {
        map.set(String(v).toLowerCase(), String(v));
      });
    }
    return map;
  }, [filter.selected]);

  const selectedValues = useMemo(() => {
    return Array.from(originalCaseMap.keys());
  }, [originalCaseMap]);

  const handleCheckboxChange = useCallback((newValues: string[]) => {
    const restored = newValues.map((v) => originalCaseMap.get(v) || v);
    onUpdateSelected(filter.name, restored);
  }, [filter.name, onUpdateSelected, originalCaseMap]);

  const handleChipClick = useCallback(() => {
    if (hasDropdown) {
      setPopoverOpened((o) => !o);
    }
  }, [hasDropdown]);

  const valuesSection = values ? (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        paddingLeft: 4,
        paddingRight: 8,
        borderRadius: '0 8px 8px 0',
        flexShrink: 1,
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <Text
        fz={12}
        fw={400}
        color="#6B7280"
        sx={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {values}
      </Text>
    </Box>
  ) : null;

  const chipContent = (
    <Box
      ref={ref}
      onClick={handleChipClick}
      sx={{
        position: 'relative',
        height: 32,
        borderRadius: 8,
        border: '1px solid #E5E7EB',
        background: '#fff',
        display: 'inline-flex',
        alignItems: 'stretch',
        cursor: hasDropdown ? 'pointer' : 'default',
        maxWidth: 240,
        flexShrink: 0,
        '&:hover': hasDropdown ? {
          borderColor: '#D1D5DB',
          background: '#FAFAFA',
        } : {},
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          paddingLeft: 8,
          paddingRight: values ? 4 : 8,
          flexShrink: 0,
        }}
      >
        <Text
          fz={12}
          fw={500}
          color="#374151"
          sx={{ whiteSpace: 'nowrap' }}
        >
          {label}
        </Text>
      </Box>

      {values && (
        <Box
          sx={{
            width: 1,
            alignSelf: 'stretch',
            margin: '7px 0',
            background: '#E5E7EB',
            flexShrink: 0,
          }}
        />
      )}

      {valuesSection}

      <Box
        sx={{
          position: 'absolute',
          right: 8,
          top: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          paddingLeft: 10,
          borderRadius: '0 8px 8px 0',
          background: hovered && !popoverOpened
            ? 'linear-gradient(to right, transparent, #fff 40%)'
            : 'none',
          opacity: hovered && !popoverOpened ? 1 : 0,
          cursor: 'pointer',
        }}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          onRemove(filter.name);
        }}
      >
        <IconX size={12} color="#9CA3AF" />
      </Box>
    </Box>
  );

  if (hasDropdown) {
    return (
      <Popover
        opened={popoverOpened}
        onChange={setPopoverOpened}
        position="bottom-start"
        shadow="md"
        radius="md"
        withinPortal
      >
        <Popover.Target>
          {chipContent}
        </Popover.Target>
        <Popover.Dropdown sx={{ padding: 0 }}>
          <ScrollArea.Autosize mah={260} sx={{ padding: 8 }}>
            <Checkbox.Group
              value={selectedValues}
              onChange={handleCheckboxChange}
            >
              {availableOptions.map((opt) => (
                <Box
                  key={opt.value}
                  sx={{ padding: '4px 0' }}
                >
                  <Checkbox
                    value={opt.value}
                    label={
                      <Text fz={12} fw={400} color="#374151">
                        {capitalizeFirstLetter(opt.label)}
                      </Text>
                    }
                    size="xs"
                    sx={{
                      '.mantine-Checkbox-input:checked': {
                        backgroundColor: '#5C5CEB',
                        borderColor: '#5C5CEB',
                      },
                    }}
                  />
                </Box>
              ))}
            </Checkbox.Group>
          </ScrollArea.Autosize>
        </Popover.Dropdown>
      </Popover>
    );
  }

  return chipContent;
};

export default AppliedFilterChip;
